import { Card } from "../ui/card";
import { Quote } from "lucide-react";
import type { TestimonialItem } from "./useTestimonialsManager";

interface TestimonialCardPreviewProps {
  item: Partial<TestimonialItem>;
}

interface SingleCardProps {
  author: string;
  role: string;
  company: string;
  quote: string;
  avatarUrl?: string;
  locale: string;
}

function SingleCard({ author, role, company, quote, avatarUrl, locale }: SingleCardProps) {
  const displayAuthor = author || (locale === "en" ? "Jane Doe" : "Ahmet Yılmaz");
  const displayRole = role || (locale === "en" ? "Product Manager" : "Ürün Yöneticisi");
  const displayCompany = company || "Tech Corp";
  const displayQuote = quote || (locale === "en" ? "This is a live preview of the client's quote. It wraps cleanly without any read more button." : "Bu, istemcinin yorumunun canlı önizlemesidir. Read more butonu olmadan temiz bir şekilde sarmalanır.");

  return (
    <Card className="bg-card text-card-foreground p-6 md:p-8 flex flex-col md:flex-row gap-6 items-center md:items-start relative border border-border/60 shadow-sm min-h-[180px] w-full text-left">
      {/* Left Column: Avatar */}
      <div className="flex-shrink-0 relative">
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt={displayAuthor}
            className="w-20 h-20 md:w-24 md:h-24 rounded-full object-cover border border-border/80 shadow-inner"
          />
        ) : (
          <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-brand-primary/5 dark:bg-brand-primary/10 border border-brand-primary/15 flex items-center justify-center text-brand-primary font-extrabold text-2xl shadow-inner uppercase">
            {displayAuthor.substring(0, 2)}
          </div>
        )}
        <div className="absolute -bottom-2 -right-2 bg-brand-primary text-white p-1.5 rounded-full shadow-md">
          <Quote className="h-4 w-4 transform rotate-180" />
        </div>
      </div>

      {/* Right Column: Content */}
      <div className="flex-1 w-full flex flex-col justify-between">
        <div>
          <h4 className="text-xl font-bold text-foreground">
            {displayAuthor}
          </h4>
          <p className="text-sm text-brand-secondary font-semibold mt-1">
            {displayRole} @ {displayCompany}
          </p>
          <p className="text-base text-muted-foreground mt-4 leading-relaxed font-sans italic whitespace-pre-wrap break-words">
            "{displayQuote}"
          </p>
        </div>
      </div>
    </Card>
  );
}

export function TestimonialCardPreview({ item }: TestimonialCardPreviewProps) {
  return (
    <div className="space-y-6">
      <div className="border-b border-border/40 pb-2">
        <h5 className="text-sm font-bold text-muted-foreground uppercase tracking-wider">Live Card Preview</h5>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-2">
          <span className="text-xs font-bold text-muted-foreground/80 uppercase tracking-wider block">English Preview</span>
          <SingleCard
            author={item.author_en || ""}
            role={item.role_en || ""}
            company={item.company_en || ""}
            quote={item.quote_en || ""}
            avatarUrl={item.avatar_url}
            locale="en"
          />
        </div>
        <div className="space-y-2">
          <span className="text-xs font-bold text-muted-foreground/80 uppercase tracking-wider block">Turkish Preview</span>
          <SingleCard
            author={item.author_tr || ""}
            role={item.role_tr || ""}
            company={item.company_tr || ""}
            quote={item.quote_tr || ""}
            avatarUrl={item.avatar_url}
            locale="tr"
          />
        </div>
      </div>
    </div>
  );
}

export default TestimonialCardPreview;
